import { fetchBillingByAppointment, updateBillingByAppointment } from "../../api/billingcodes";
import { fetchSoapNotes } from "../../api/soap";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useState, useEffect } from "react";

import ReactMarkdown from "react-markdown";
import LoadingCard from "./LoadingCard";
import { ReceiptText } from "lucide-react";
import PostCallFallback from "./PostCallFallback";
import {
  buildIcdOrderViewModel,
  extractOrdersFromSoapResponse,
  getIcdDisplayLabel,
} from "../../utils/icdOrderMapping";

const getBillingPayload = (response) =>
  response?.data?.data || response?.data || response || {};

const hasBillingDocument = (response) => {
  const billingData = getBillingPayload(response);
  const billingText = billingData.billing_codes || billingData.engine_v1_gpt || "";
  const candidates = billingData.engine_v2_search?.cpt_candidates;
  return (typeof billingText === "string" && billingText.trim().length > 0) ||
    (Array.isArray(candidates) && candidates.some((candidate) => candidate?.code));
};

const hasSoapText = (response) =>
  String(
    response?.data?.data?.data?.soap_notes ??
    response?.data?.data?.soap_notes ??
    response?.data?.soap_notes ??
    response?.soap_notes ??
    ""
  ).trim().length > 0;

const getSoapNotesText = (response) =>
  response?.data?.data?.data?.soap_notes ??
  response?.data?.data?.soap_notes ??
  response?.data?.soap_notes ??
  response?.soap_notes ??
  "";

const Billing = ({
  appointmentId,
  username,
  canEdit = true,
  callEndTime,
  postCallWindowExpired = false,
  finalFetchStatus = "idle",
}) => {
  const queryKey = ["billing-codes", appointmentId, username];
  const [isEditing, setIsEditing] = useState(false);
  const [billingCodes, setBillingCodes] = useState("");
  const [amaCptCandidates, setAmaCptCandidates] = useState([]);
  const [soapOrders, setSoapOrders] = useState([]);
  const [billingResponse, setBillingResponse] = useState(null);
  const endTimestamp = callEndTime ? new Date(callEndTime).getTime() : NaN;
  const deadlineExpired =
    Boolean(postCallWindowExpired) ||
    (Number.isFinite(endTimestamp) &&
      Date.now() >= endTimestamp + 10 * 60 * 1000);

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey,
    queryFn: async ({ signal }) => {
      const response = await fetchBillingByAppointment(
        `${username}_${appointmentId}_billing`,
        username,
        signal
      );
      if (!hasBillingDocument(response)) {
        throw new Error("Billing data is not available yet.");
      }
      return response;
    },
    enabled: Boolean(username && appointmentId && !deadlineExpired),
    retry: 3,
    staleTime: Infinity,
    gcTime: Infinity,
    retryOnMount: true,
    refetchOnMount: "always",
    refetchOnWindowFocus: false,
    refetchInterval: false,
  });

  const { data: soapData } = useQuery({
    queryKey: ["soap-notes", appointmentId, username],
    queryFn: async ({ signal }) => {
      const response = await fetchSoapNotes(
        `${username}_${appointmentId}_soap`,
        username,
        signal
      );
      if (!hasSoapText(response)) {
        throw new Error("SOAP notes are not available yet.");
      }
      return response;
    },
    enabled: Boolean(username && appointmentId && !deadlineExpired),
    retry: 3,
    staleTime: Infinity,
    gcTime: Infinity,
    retryOnMount: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchInterval: false,
  });


  useEffect(() => {
    // Backward compatible: some docs store markdown in billing_codes, others in engine_v1_gpt
    const billingPayload = getBillingPayload(data);
    const combined = billingPayload.billing_codes || billingPayload.engine_v1_gpt || "";
    setBillingCodes(combined);
    setBillingResponse(data);

    // AMA CPT candidates (ai_search_v1)
    const candidates = billingPayload.engine_v2_search?.cpt_candidates;
    setAmaCptCandidates(Array.isArray(candidates) ? candidates : []);
  }, [data]);

  useEffect(() => {
    setSoapOrders(extractOrdersFromSoapResponse(soapData));
  }, [soapData]);

  const billingViewModel = buildIcdOrderViewModel({
    orders: soapOrders,
    billingSource: billingResponse,
    soapNotes: getSoapNotesText(soapData),
  });

  const amaCptMarkdown =
    Array.isArray(amaCptCandidates) && amaCptCandidates.length > 0
      ? `CPT codes (AMA):\n\n${amaCptCandidates
        .filter(c => c?.code)
        .map(c => `- ${c.code}${c.reason ? `: ${c.reason}` : ""}`)
        .join("\n")}\n\n`
      : "";

  const displayBillingCodes = `${amaCptMarkdown}${billingCodes || ""}`;
  const icdGroups = billingViewModel.diagnosisGroups || [];

  const mutation = useMutation({
    mutationFn: (updatedCodes) =>
      updateBillingByAppointment(`${username}_${appointmentId}_billing`, username, updatedCodes),
    onSuccess: () => {
      if (!deadlineExpired) refetch();
      setIsEditing(false);
    },
    onError: () => {
      console.log("Failed to save billing codes.");
    },
  });

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleSave = () => {
    if (!billingCodes || billingCodes.trim() === "") {
      alert("Billing code cannot be empty.");
      return;
    }

    mutation.mutate(billingCodes);
  };

  const billingAvailable = hasBillingDocument(data);
  const finalFetchPending =
    deadlineExpired && !billingAvailable && finalFetchStatus === "fetching";

  if (isLoading || (!deadlineExpired && isFetching) || finalFetchPending) {
    return <LoadingCard message="Running the diagnosis-to-dollars pipeline… Billing Codes coming right up!" />;
  }


  if (!billingAvailable) {
    return (
      <PostCallFallback
        title="Billing Codes"
        icon={<ReceiptText size={20} />}
        message={
          deadlineExpired
            ? "No billing data available."
            : "Billing data is not available yet. You can try again."
        }
        deadlineExpired={deadlineExpired}
        onRetry={() => refetch()}
        isRetrying={isFetching}
        retryLabel="Retry Billing"
        retryingLabel="Retrying Billing…"
      />
    );
  }

  return (
    <div className="space-y-4">
      {!isEditing && (
        <>
          <div className="space-y-4">
            <div className="grid gap-4">
              {icdGroups.length > 0 ? (
                icdGroups.map((group) => (
                  <div key={group.code} className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div>
                        <div className="text-xs font-semibold uppercase tracking-[0.22em] text-black">ICD-10</div>
                        <div className="mt-2 text-lg font-semibold text-slate-900">
                          {getIcdDisplayLabel(group)}
                        </div>
                      </div>
                      <span className="inline-flex rounded-full border border-slate-200 bg-slate-100 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-slate-900">
                        {group.relatedOrders?.length || 0} related order{group.relatedOrders?.length === 1 ? "" : "s"}
                      </span>
                    </div>

                    <div className="mt-4">
                      <div className="text-xs font-semibold uppercase tracking-[0.18em] text-black">Related Orders</div>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {(group.relatedOrders || []).length > 0 ? (
                          group.relatedOrders.map((entry) => (
                            <span key={entry.orderKey} className="inline-flex items-center rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-sm font-medium text-blue-700">
                              {entry.order?.selected_order_name || entry.order?.clinical_intent || entry.order?.name || entry.order?.order_type || "Order"}
                            </span>
                          ))
                        ) : (
                          <span className="text-sm text-black italic">No related orders found.</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4 text-sm text-black italic">
                  No ICD-10 codes were found in the billing response.
                </div>
              )}
            </div>

            <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm text-black text-[15px] leading-relaxed">
              <ReactMarkdown>{displayBillingCodes}</ReactMarkdown>
            </div>
          </div>
          {canEdit && (
            <button
              onClick={handleEditClick}
              className="bg-yellow-600 text-white px-4 py-2 rounded hover:bg-yellow-700"
            >
              Edit
            </button>
          )}
        </>
      )}

      {isEditing && (
        <>
          <label className="block text-sm font-medium text-gray-700">Edit Billing Codes</label>
          <textarea
            className="w-full border border-gray-300 rounded p-2"
            rows={6}
            value={billingCodes}
            onChange={(e) => setBillingCodes(e.target.value)}
            placeholder="Enter billing codes (e.g., CPT: 99213, ICD-10: E11.9)"
          />
          <div className="flex gap-4 mt-2">
            <button
              onClick={handleSave}
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50"
              disabled={mutation.isLoading}
            >
              {mutation.isLoading ? "Saving..." : "Save"}
            </button>
            <button
              onClick={() => {
                setIsEditing(false);
                const billingPayload = getBillingPayload(data);
                setBillingCodes(billingPayload.billing_codes || billingPayload.engine_v1_gpt || "");
              }}
              className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
            >
              Cancel
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default Billing;
